export type AddressValidationStatus = 'NOTDELIVERABLE' | 'MISMATCH' | 'DELIVERABLE' | 'DELIVERABILISH';

const cleanStatus = (value: unknown) =>
  String(value ?? '')
    .replace(/[^a-z]/gi, '')
    .toUpperCase();

export const getAddressId = (address: any) => address?.id ?? address?.addressId ?? address?.key ?? address?.addressKey;

const getObjectValueByNormalizedKey = (item: any, keys: string[]) => {
  if (!item || typeof item !== 'object') return undefined;

  for (const [key, value] of Object.entries(item)) {
    const normalizedKey = cleanStatus(key);
    if (keys.includes(normalizedKey)) return value;
  }

  return undefined;
};

const findNestedValueByNormalizedKey = (item: any, keys: string[], seen = new WeakSet<object>()): unknown => {
  if (!item || typeof item !== 'object') return undefined;
  if (seen.has(item)) return undefined;
  seen.add(item);

  const directValue = getObjectValueByNormalizedKey(item, keys);
  if (directValue !== undefined && directValue !== null && directValue !== '') return directValue;

  for (const value of Object.values(item)) {
    if (value && typeof value === 'object') {
      const nestedValue = findNestedValueByNormalizedKey(value, keys, seen);
      if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') return nestedValue;
    }
  }

  return undefined;
};

const findNestedValidationArray = (item: any, seen = new WeakSet<object>()): any[] | undefined => {
  if (!item || typeof item !== 'object') return undefined;
  if (seen.has(item)) return undefined;
  seen.add(item);

  for (const value of Object.values(item)) {
    if (Array.isArray(value)) {
      const hasValidationPayload = value.some(
        (entry) =>
          entry &&
          typeof entry === 'object' &&
          (findNestedValueByNormalizedKey(entry, ['POSSIBLENEXTACTION', 'POSSIBLEEXTRACTION', 'VALIDATIONSTATUS']) ||
            getObjectValueByNormalizedKey(entry, ['ISVALID']) !== undefined),
      );

      if (hasValidationPayload) return value;
    }

    if (value && typeof value === 'object') {
      const nestedArray = findNestedValidationArray(value, seen);
      if (nestedArray) return nestedArray;
    }
  }

  return undefined;
};

export const normalizeAddressValidationStatus = (value: unknown): AddressValidationStatus => {
  const status = cleanStatus(value);

  if (status.includes('MISMATCH')) return 'MISMATCH';
  if (status.includes('NOTDELIVERABLE') || status.includes('UNDELIVERABLE')) return 'NOTDELIVERABLE';
  if (status.includes('DELIVERABILISH')) return 'DELIVERABILISH';

  return 'DELIVERABLE';
};

export const getAddressValidationStatus = (item: any): AddressValidationStatus => {
  const candidates = [
    item?.possibleNextAction,
    item?.possible_next_action,
    item?.possibleextraction,
    item?.validationStatus,
    item?.status,
    item?.verdict,
    item?.result?.possibleNextAction,
    item?.addressValidation?.possibleNextAction,
    findNestedValueByNormalizedKey(item, ['POSSIBLENEXTACTION', 'POSSIBLEEXTRACTION', 'VALIDATIONSTATUS']),
  ];

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && candidate !== '') {
      const status = normalizeAddressValidationStatus(candidate);
      if (status !== 'DELIVERABLE') return status;
    }
  }

  return item?.isValid === false ? 'NOTDELIVERABLE' : 'DELIVERABLE';
};

export const getAddressValidationSequence = (item: any) =>
  findNestedValueByNormalizedKey(item, ['ADDRESSSEQUENCE', 'SEQUENCE', 'REQUESTID']);

export const getAddressValidationItems = (data: any): any[] => {
  if (Array.isArray(data)) return data;

  const nestedArray = [
    data?.data,
    data?.results,
    data?.result,
    data?.items,
    data?.addresses,
    data?.validationResults,
    data?.addressValidationResults,
    data?.response,
    data?.response?.data,
    data?.response?.results,
  ].find(Array.isArray);

  if (nestedArray) return nestedArray;

  const deeplyNestedArray = findNestedValidationArray(data);
  if (deeplyNestedArray) return deeplyNestedArray;

  return data ? [data] : [];
};

export const isInvalidAddressValidationStatus = (value: unknown) => {
  const status = normalizeAddressValidationStatus(value);
  return status === 'NOTDELIVERABLE' || status === 'MISMATCH';
};
